import { locale, t } from '../i18n/i18n';
import { type CSSProperties, type ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { type CloudProjectSummary, deleteCloudProject, duplicateCloudProject, listCloudProjects, loadCloudProject, openCloudProject, patchCloudProject, uploadLocalProject } from '../account/actions';
import { type ProjectSummary, deleteProject, duplicateProject, listProjects, loadProject, moveProject, renameProject } from '../project/db';
import { allFolders, loadFolderList, saveFolderList } from '../project/folders';
import { PROJECT_EXTENSION, normalizeFolder } from '../project/format';
import { store, useApp } from '../state/store';
import { downloadProject, importProjectFile, openProject, saveCurrentProject, startNewProject } from './actions';
import { IconClose, IconCloud, IconCopy, IconDevice, IconExport, IconFolder, IconImport, IconMore, IconPlus, IconSave, IconTrash, IconUser } from './icons';

/** One project, wherever it is stored: in this browser, in the cloud, or both (same id). */
interface Entry {
  id: string;
  name: string;
  folder: string | null;
  updatedAt: string;
  thumbnail: string | null;
  objectCount: number;
  local: ProjectSummary | null;
  cloud: CloudProjectSummary | null;
}

function merge(local: ProjectSummary[], cloud: CloudProjectSummary[]): Entry[] {
  const map = new Map<string, Entry>();
  for (const p of local) map.set(p.id, { ...p, local: p, cloud: null });
  for (const c of cloud) {
    const e = map.get(c.id);
    if (!e) map.set(c.id, { ...c, local: null, cloud: c });
    // the newest copy decides what the card shows
    else map.set(c.id, c.updatedAt > e.updatedAt ? { ...c, thumbnail: c.thumbnail ?? e.thumbnail, local: e.local, cloud: c } : { ...e, cloud: c });
  }
  return [...map.values()].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

/** null = all projects, '' = projects without a folder, otherwise a folder name. */
type FolderFilter = string | null;

export function ProjectsDialog() {
  const user = useApp((s) => s.account.user);
  const accounts = useApp((s) => s.account.enabled);
  const current = useApp((s) => s.project);
  const [local, setLocal] = useState<ProjectSummary[] | null>(null);
  const [cloud, setCloud] = useState<CloudProjectSummary[]>([]);
  const [remembered, setRemembered] = useState<string[]>(loadFolderList);
  const [filter, setFilter] = useState<FolderFilter>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const close = () => store.set({ panel: null });

  const refresh = async () => {
    const [l, c] = await Promise.all([listProjects(), user ? listCloudProjects() : Promise.resolve([])]);
    setLocal(l);
    setCloud(c);
  };
  useEffect(() => {
    refresh().catch((e) => setError(e instanceof Error ? e.message : String(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const guard = async (f: () => Promise<unknown>) => {
    setError(null);
    setBusy(true);
    try {
      await f();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const remember = (list: string[]) => {
    setRemembered(list);
    saveFolderList(list);
  };

  const entries = useMemo(() => merge(local ?? [], cloud), [local, cloud]);
  const folders = useMemo(() => allFolders(remembered, entries.map((e) => e.folder), locale()), [remembered, entries]);
  const shown = entries.filter((e) => filter === null || (e.folder ?? '') === filter);
  const count = (f: FolderFilter) => entries.filter((e) => f === null || (e.folder ?? '') === f).length;
  const localOnly = user ? entries.filter((e) => e.local && !e.cloud) : [];

  const confirmDiscard = () => !current.dirty || window.confirm(t('Changes to the current project are not saved. Discard them?'));

  /** Moves projects (in the browser and in the cloud) and keeps the open project in step. */
  const move = async (list: Entry[], folder: string | null) => {
    for (const e of list) {
      if (e.local) await moveProject(e.id, folder);
      if (e.cloud) await patchCloudProject(e.id, { folder });
    }
    if (list.some((e) => e.id === current.id)) store.set((s) => ({ project: { ...s.project, folder } }));
  };

  const save = () =>
    guard(async () => {
      // a project that has not been saved yet goes into the folder being looked at
      if (filter && !current.id) store.set((s) => ({ project: { ...s.project, folder: filter } }));
      await saveCurrentProject();
      await refresh();
    });

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="projects-title" onKeyDown={(e) => e.key === 'Escape' && close()}>
      <div className="modal">
        <div className="modal-head">
          <h2 id="projects-title">{accounts ? t('Projects') : t('Projects on this device')}</h2>
          <button className="btn icon ghost" aria-label={t('Close dialog')} onClick={close}>
            <IconClose />
          </button>
        </div>
        <div className="modal-body">
          <div className="row wrap" style={{ marginBottom: 12 }}>
            <button className="btn primary" disabled={busy} onClick={save}>
              <IconSave size={14} /> {t('Save current project')}
            </button>
            <button className="btn" onClick={() => startNewProject()}>
              <IconPlus size={14} /> {t('New project')}
            </button>
            <button className="btn" onClick={() => fileInput.current?.click()}>
              <IconImport size={14} /> {t('Import project file')}
            </button>
            <input
              ref={fileInput}
              type="file"
              accept={`.json,${PROJECT_EXTENSION},application/json`}
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (f && confirmDiscard()) void guard(() => importProjectFile(f));
              }}
            />
          </div>
          {user ? (
            <p className="hint">{t('You are logged in as {email}: projects are saved in the cloud, with a copy in this browser.', { email: user.email })}</p>
          ) : accounts ? (
            <p className="hint">
              {t('Projects are saved in this browser.')}{' '}
              <button className="link-button" onClick={() => store.set({ panel: 'account' })}>
                <IconUser size={12} /> {t('Log in to save them in the cloud.')}
              </button>
            </p>
          ) : (
            <p className="hint">{t('Projects are stored only in this browser (IndexedDB). Download the project file as a backup or to move the project to another computer.')}</p>
          )}
          {localOnly.length > 0 && (
            <p className="issue info">
              {t('{n} projects are only in this browser.', { n: localOnly.length })}{' '}
              <button className="btn small" disabled={busy} onClick={() => guard(async () => { for (const e of localOnly) await uploadLocalProject(e.id); await refresh(); })}>
                <IconCloud size={14} /> {t('Upload them to the cloud')}
              </button>
            </p>
          )}
          {error && (
            <p className="issue error" role="alert">
              {error}
            </p>
          )}
          <div className="projects-layout">
            <FolderList folders={folders} filter={filter} setFilter={setFilter} count={count} onCreate={(name) => { remember([...remembered, name]); setFilter(name); }} />
            <div className="projects-main">
              {filter && (
                <FolderHeader
                  name={filter}
                  busy={busy}
                  onRename={(to) =>
                    guard(async () => {
                      await move(entries.filter((e) => e.folder === filter), to);
                      remember([...remembered.filter((f) => f !== filter), to]);
                      setFilter(to);
                      await refresh();
                    })
                  }
                  onDelete={() =>
                    guard(async () => {
                      await move(entries.filter((e) => e.folder === filter), null);
                      remember(remembered.filter((f) => f !== filter));
                      setFilter(null);
                      await refresh();
                    })
                  }
                />
              )}
              {local === null ? (
                <p className="hint">{t('Loading …')}</p>
              ) : shown.length === 0 ? (
                <div className="empty">
                  <strong>{filter ? t('The folder is empty') : t('No saved projects')}</strong>
                  {filter ? t('Move projects here with “Move to folder”, or save the current project while the folder is open.') : t('Save what you are working on and it will show up here.')}
                </div>
              ) : (
                <div className="project-grid">
                  {shown.map((e) => (
                    <ProjectCard
                      key={e.id}
                      e={e}
                      folders={folders}
                      busy={busy}
                      loggedIn={!!user}
                      guard={guard}
                      refresh={refresh}
                      move={move}
                      confirmDiscard={confirmDiscard}
                      newFolder={(name) => remember([...remembered, name])}
                    />
                  ))}
                </div>
              )}
              {entries.length > 0 && (
                <p className="hint project-legend">
                  <IconDevice size={13} /> {t('In this browser')} · <IconCloud size={13} /> {t('In the cloud')}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FolderList({ folders, filter, setFilter, count, onCreate }: { folders: string[]; filter: FolderFilter; setFilter: (f: FolderFilter) => void; count: (f: FolderFilter) => number; onCreate: (name: string) => void }) {
  const [creating, setCreating] = useState<string | null>(null);
  const item = (f: FolderFilter, label: string) => (
    <li key={f === null ? '\u0000all' : f === '' ? '\u0000none' : f}>
      <button className="folder-item" aria-pressed={filter === f} onClick={() => setFilter(f)}>
        <span className="folder-name">{label}</span>
        <span className="folder-count">{count(f)}</span>
      </button>
    </li>
  );
  return (
    <nav className="folder-list" aria-label={t('Folders')}>
      <ul>
        {item(null, t('All projects'))}
        {folders.map((f) => item(f, f))}
        {item('', t('No folder'))}
      </ul>
      {creating === null ? (
        <button className="btn small ghost" onClick={() => setCreating('')}>
          <IconPlus size={12} /> {t('New folder')}
        </button>
      ) : (
        <form
          className="row"
          onSubmit={(e) => {
            e.preventDefault();
            const name = normalizeFolder(creating);
            if (name) onCreate(name);
            setCreating(null);
          }}
        >
          <input
            className="input"
            autoFocus
            aria-label={t('Folder name')}
            placeholder={t('Folder name')}
            value={creating}
            onChange={(e) => setCreating(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.stopPropagation();
                setCreating(null);
              }
            }}
          />
          <button className="btn small" type="submit">
            {t('Create')}
          </button>
        </form>
      )}
    </nav>
  );
}

function FolderHeader({ name, busy, onRename, onDelete }: { name: string; busy: boolean; onRename: (to: string) => void; onDelete: () => void }) {
  const [renaming, setRenaming] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  return (
    <div className="folder-header row wrap">
      <IconFolder size={16} />
      {renaming === null ? (
        <h3>{name}</h3>
      ) : (
        <form
          className="row"
          onSubmit={(e) => {
            e.preventDefault();
            const to = normalizeFolder(renaming);
            if (to && to !== name) onRename(to);
            setRenaming(null);
          }}
        >
          <input
            className="input"
            autoFocus
            aria-label={t('Folder name')}
            value={renaming}
            onChange={(e) => setRenaming(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.stopPropagation();
                setRenaming(null);
              }
            }}
          />
          <button className="btn small" type="submit">
            {t('Save')}
          </button>
        </form>
      )}
      <span className="spacer" />
      {renaming === null && (
        <button className="btn small" disabled={busy} onClick={() => setRenaming(name)}>
          {t('Rename folder')}
        </button>
      )}
      {confirm ? (
        <>
          <button className="btn small danger" disabled={busy} onClick={onDelete}>
            {t('Delete the folder (the projects are kept)')}
          </button>
          <button className="btn small ghost" onClick={() => setConfirm(false)}>
            {t('Cancel')}
          </button>
        </>
      ) : (
        <button className="btn small icon danger" aria-label={t('Delete folder')} title={t('Delete folder')} onClick={() => setConfirm(true)}>
          <IconTrash size={14} />
        </button>
      )}
    </div>
  );
}

interface CardProps {
  e: Entry;
  folders: string[];
  busy: boolean;
  loggedIn: boolean;
  guard: (f: () => Promise<unknown>) => Promise<void>;
  refresh: () => Promise<void>;
  move: (list: Entry[], folder: string | null) => Promise<void>;
  confirmDiscard: () => boolean;
  newFolder: (name: string) => void;
}

function ProjectCard({ e, folders, busy, loggedIn, guard, refresh, move, confirmDiscard, newFolder }: CardProps) {
  const current = useApp((s) => s.project);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [menu, setMenu] = useState<'main' | 'move' | 'delete' | null>(null);
  const isOpen = current.id === e.id;
  const newest = (): 'cloud' | 'local' => (e.cloud && (!e.local || e.cloud.updatedAt >= e.local.updatedAt) ? 'cloud' : 'local');

  const open = () => {
    if (!confirmDiscard()) return;
    void guard(async () => {
      // open the newest copy; a project that is in the cloud keeps saving there
      if (newest() === 'cloud') await openCloudProject(e.id);
      else {
        await openProject(e.id);
        if (e.cloud) store.set((s) => ({ project: { ...s.project, cloud: true } }));
      }
    });
  };

  const run = (f: () => Promise<unknown>) => {
    setMenu(null);
    void guard(async () => {
      await f();
      await refresh();
    });
  };

  const rename = (name: string) =>
    run(async () => {
      if (e.local) await renameProject(e.id, name);
      if (e.cloud) await patchCloudProject(e.id, { name });
      if (isOpen) store.set((s) => ({ project: { ...s.project, name } }));
      setRenaming(null);
    });

  const remove = (where: 'local' | 'cloud' | 'both') =>
    run(async () => {
      if (e.local && where !== 'cloud') await deleteProject(e.id);
      if (e.cloud && where !== 'local') await deleteCloudProject(e.id);
      const gone = where === 'both' || (where === 'local' && !e.cloud) || (where === 'cloud' && !e.local);
      if (isOpen && gone) store.set((s) => ({ project: { ...s.project, id: null, cloud: false, dirty: true } }));
    });

  // logged in, a copy goes to the cloud like everything else that is saved
  const copy = () =>
    run(async () => {
      if (e.local) {
        const id = await duplicateProject(e.id);
        if (loggedIn) await uploadLocalProject(id);
      } else await duplicateCloudProject(e.id);
    });

  const download = () => run(async () => downloadProject(newest() === 'cloud' ? await loadCloudProject(e.id) : await loadProject(e.id)));

  return (
    <div className="project-card">
      <button className="thumb" style={{ border: 0, padding: 0 }} onClick={open} aria-label={t('Open {name}', { name: e.name })}>
        {e.thumbnail ? <img src={e.thumbnail} alt="" /> : <span className="hint">{t('No preview')}</span>}
      </button>
      <div className="meta">
        <div className="card-title">
          {renaming !== null ? (
            <input
              className="input"
              autoFocus
              aria-label={t('Project name')}
              value={renaming}
              onChange={(ev) => setRenaming(ev.target.value)}
              onBlur={() => setRenaming(null)}
              onKeyDown={(ev) => {
                if (ev.key === 'Enter' && renaming.trim()) rename(renaming.trim());
                if (ev.key === 'Escape') {
                  ev.stopPropagation();
                  setRenaming(null);
                }
              }}
            />
          ) : (
            <b title={t('Double-click to rename')} onDoubleClick={() => setRenaming(e.name)}>
              {e.name} {isOpen && <span className="badge ok">{t('open')}</span>}
            </b>
          )}
          <button className="btn small icon ghost" aria-label={t('More actions for {name}', { name: e.name })} aria-haspopup="menu" aria-expanded={menu !== null} disabled={busy} onClick={() => setMenu(menu ? null : 'main')}>
            <IconMore size={16} />
          </button>
          {menu && (
            <CardMenu onClose={() => setMenu(null)}>
              {menu === 'main' && (
                <>
                  <MenuItem
                    onClick={() => {
                      setMenu(null);
                      setRenaming(e.name);
                    }}
                  >
                    {t('Rename')}
                  </MenuItem>
                  <MenuItem onClick={() => setMenu('move')}>
                    <IconFolder size={14} /> {t('Move to folder')} …
                  </MenuItem>
                  <MenuItem onClick={copy}>
                    <IconCopy size={14} /> {t('Make a copy')}
                  </MenuItem>
                  <MenuItem onClick={download}>
                    <IconExport size={14} /> {t('Download as project file')}
                  </MenuItem>
                  {loggedIn && e.local && !e.cloud && (
                    <MenuItem onClick={() => run(() => uploadLocalProject(e.id))}>
                      <IconCloud size={14} /> {t('Upload to the cloud')}
                    </MenuItem>
                  )}
                  <MenuItem danger onClick={() => setMenu('delete')}>
                    <IconTrash size={14} /> {t('Delete')} …
                  </MenuItem>
                </>
              )}
              {menu === 'move' && <MoveMenu folders={folders} current={e.folder} onPick={(f) => run(() => move([e], f))} onCreate={(name) => { newFolder(name); run(() => move([e], name)); }} />}
              {menu === 'delete' &&
                (e.local && e.cloud ? (
                  <>
                    <MenuItem danger onClick={() => remove('both')}>
                      {t('Delete everywhere')}
                    </MenuItem>
                    <MenuItem onClick={() => remove('local')}>{t('Only from this browser')}</MenuItem>
                    <MenuItem onClick={() => remove('cloud')}>{t('Only from the cloud')}</MenuItem>
                  </>
                ) : (
                  <MenuItem danger onClick={() => remove('both')}>
                    {t('Delete “{name}”', { name: e.name })}
                  </MenuItem>
                ))}
            </CardMenu>
          )}
        </div>
        <small className="project-info">
          <span className="where">
            {e.local && (
              <span title={t('In this browser')} aria-label={t('In this browser')} role="img">
                <IconDevice size={13} />
              </span>
            )}
            {e.cloud && (
              <span title={t('In the cloud')} aria-label={t('In the cloud')} role="img">
                <IconCloud size={13} />
              </span>
            )}
          </span>
          {new Date(e.updatedAt).toLocaleString(locale())} · {t('{n} objects', { n: e.objectCount })}
          {e.folder && (
            <span className="card-folder">
              <IconFolder size={12} /> {e.folder}
            </span>
          )}
        </small>
      </div>
    </div>
  );
}

/**
 * Small popup menu in a project card, placed under its ⋯ button (above it when there is no room
 * below) so the dialog never clips it. Closes on a click outside, Escape or scrolling.
 */
function CardMenu({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<CSSProperties>({ visibility: 'hidden' });
  useLayoutEffect(() => {
    const menu = ref.current;
    const button = menu?.parentElement?.querySelector('[aria-haspopup="menu"]');
    if (!menu || !button) return;
    const b = button.getBoundingClientRect();
    const h = menu.offsetHeight, w = menu.offsetWidth;
    const below = b.bottom + 4 + h <= window.innerHeight - 8;
    setPos({
      position: 'fixed',
      top: below ? b.bottom + 4 : Math.max(8, b.top - 4 - h),
      left: Math.max(8, Math.min(b.right - w, window.innerWidth - w - 8)),
      right: 'auto',
    });
  }, [children]);
  useEffect(() => {
    // clicks inside the card (including its ⋯ button, which toggles) are not "outside"
    const outside = (ev: PointerEvent) => {
      const card = ref.current?.closest('.project-card');
      if (card && !card.contains(ev.target as Node)) onClose();
    };
    const scrolled = (ev: Event) => !ref.current?.contains(ev.target as Node) && onClose();
    window.addEventListener('pointerdown', outside);
    window.addEventListener('scroll', scrolled, true);
    window.addEventListener('resize', onClose);
    return () => {
      window.removeEventListener('pointerdown', outside);
      window.removeEventListener('scroll', scrolled, true);
      window.removeEventListener('resize', onClose);
    };
  }, [onClose]);
  return (
    <div
      ref={ref}
      className="card-menu"
      style={pos}
      role="menu"
      onKeyDown={(ev) => {
        if (ev.key === 'Escape') {
          ev.stopPropagation();
          onClose();
        }
      }}
    >
      {children}
    </div>
  );
}

function MenuItem({ onClick, danger, children }: { onClick: () => void; danger?: boolean; children: ReactNode }) {
  return (
    <button role="menuitem" className={'card-menu-item' + (danger ? ' danger' : '')} onClick={onClick}>
      {children}
    </button>
  );
}

function MoveMenu({ folders, current, onPick, onCreate }: { folders: string[]; current: string | null; onPick: (folder: string | null) => void; onCreate: (name: string) => void }) {
  const [creating, setCreating] = useState<string | null>(null);
  const item = (f: string | null, label: string) => (
    <button key={f ?? '\u0000none'} role="menuitemradio" aria-checked={current === f} className="card-menu-item" onClick={() => onPick(f)}>
      <span className="check-mark">{current === f ? '✓' : ''}</span> {label}
    </button>
  );
  return (
    <>
      {item(null, t('No folder'))}
      {folders.map((f) => item(f, f))}
      {creating === null ? (
        <MenuItem onClick={() => setCreating('')}>
          <IconPlus size={14} /> {t('New folder')} …
        </MenuItem>
      ) : (
        <form
          className="row"
          style={{ padding: 4 }}
          onSubmit={(ev) => {
            ev.preventDefault();
            const name = normalizeFolder(creating);
            if (name) onCreate(name);
          }}
        >
          <input className="input" autoFocus aria-label={t('Folder name')} placeholder={t('Folder name')} value={creating} onChange={(ev) => setCreating(ev.target.value)} />
          <button className="btn small" type="submit">
            {t('Create')}
          </button>
        </form>
      )}
    </>
  );
}
